/** @jsxImportSource @emotion/react */
import { memo } from "react";
import { EuiSuperSelect } from "@inscopix/ideas-eui";
import { gql } from "@apollo/client";
import { useCellEditorToolVersionQuery } from "graphql/_Types";

gql`
  query CellEditorToolVersion($toolVersionId: Int!) {
    toolVersion: toolVersionById(id: $toolVersionId) {
      id
      compatibleVersions {
        nodes {
          id
          version
          toolSpec
          credits
          status
        }
      }
    }
  }
`;

interface ToolVersionSelectorBaseProps {
  defaultVersion: { id: number };
  selectedToolVersion: { id: number; version: string };
  onSelectVersion: (selectedVersion: { id: number; version: string }) => void;
}

/** Cell editor for cells in the tool version column of an analysis table */
export const ToolVersionSelectorBase = memo(function ToolVersionSelectorBase({
  selectedToolVersion,
  defaultVersion,
  onSelectVersion,
}: ToolVersionSelectorBaseProps) {
  const { data, loading } = useCellEditorToolVersionQuery({
    fetchPolicy: "cache-first",
    variables: {
      toolVersionId: defaultVersion.id,
    },
  });
  const compatibleToolVersions = data?.toolVersion?.compatibleVersions.nodes;

  const selected = selectedToolVersion ?? defaultVersion;
  const options = (compatibleToolVersions ?? [selected]).map(
    ({ id, version }) => ({ value: id.toString(), inputDisplay: version }),
  );

  return (
    <EuiSuperSelect
      compressed
      aria-label="Tool version"
      options={options}
      valueOfSelected={selected.id.toString()}
      isLoading={loading}
      onChange={(value) => {
        const match = compatibleToolVersions?.find(
          ({ id }) => id.toString() === value,
        );
        if (match !== undefined) {
          onSelectVersion({ id: match.id, version: match.version });
        }
      }}
    />
  );
});
